import type { Schemas } from '@kanos/api-client';
import { api, failure } from '@kanos/web-core';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

export type PartnerAppAdmin = Schemas['PartnerAppAdmin'];
export type PartnerAppRegistration = Schemas['PartnerAppRegistration'];
export type PlatformStaffMember = Schemas['PlatformStaffMember'];

const partnerAppsKey = ['platform', 'partner-apps'] as const;
const staffKey = ['platform', 'staff'] as const;

/** Every partner app, suspended ones included (ADR-016). */
export const partnerAppsQuery = queryOptions({
  queryKey: partnerAppsKey,
  queryFn: async (): Promise<PartnerAppAdmin[]> => {
    const { data, error, response } = await api.GET('/api/v1/platform/partner-apps');
    if (!data) throw failure('listPartnerAppsForPlatform', error, response.status);
    return data;
  },
});

/** Everyone who signed in to the platform, and who of them administers it (ADR-016). */
export const staffQuery = queryOptions({
  queryKey: staffKey,
  queryFn: async (): Promise<PlatformStaffMember[]> => {
    const { data, error, response } = await api.GET('/api/v1/platform/staff');
    if (!data) throw failure('listPlatformStaff', error, response.status);
    return data;
  },
});

/** A change on the platform; what it changed is read again afterwards. */
function useChange<T, R>(queryKey: readonly string[], mutationFn: (input: T) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });
}

const usePartnerAppChange = <T, R>(mutationFn: (input: T) => Promise<R>) =>
  useChange(partnerAppsKey, mutationFn);

/** Makes a person of the staff an administrator, or takes it back. */
export const useAdminChange = () =>
  useChange(staffKey, async ({ subject, admin }: { subject: string; admin: boolean }) => {
    const path = { params: { path: { subject } } };
    const { data, error, response } = admin
      ? await api.PUT('/api/v1/platform/staff/{subject}/admin', path)
      : await api.DELETE('/api/v1/platform/staff/{subject}/admin', path);
    if (!data)
      throw failure(admin ? 'grantPlatformAdmin' : 'revokePlatformAdmin', error, response.status);
    return data;
  });

export const useRegisterPartnerApp = () =>
  usePartnerAppChange(async (body: PartnerAppRegistration): Promise<PartnerAppAdmin> => {
    const { data, error, response } = await api.POST('/api/v1/platform/partner-apps', { body });
    if (!data) throw failure('registerPartnerAppOnPlatform', error, response.status);
    return data;
  });

export const useReleasePartnerApp = () =>
  usePartnerAppChange(
    async ({ id, ...body }: { id: string; dpaReference: string; reviewedBy: string }) => {
      const { data, error, response } = await api.POST(
        '/api/v1/platform/partner-apps/{partnerAppId}/release',
        { params: { path: { partnerAppId: id } }, body },
      );
      if (!data) throw failure('releasePartnerAppOnPlatform', error, response.status);
      return data;
    },
  );

export const useSuspendPartnerApp = () =>
  usePartnerAppChange(async ({ id, reason }: { id: string; reason: string }) => {
    const { data, error, response } = await api.POST(
      '/api/v1/platform/partner-apps/{partnerAppId}/suspension',
      { params: { path: { partnerAppId: id } }, body: { reason } },
    );
    if (!data) throw failure('suspendPartnerAppOnPlatform', error, response.status);
    return data;
  });

export type FaqEntry = Schemas['FaqEntry'];
export type FaqEntryInput = Schemas['FaqEntryInput'];

const faqKey = ['platform', 'faq'] as const;

/** Every question of the help, unpublished ones included (ADR-021 §6). */
export const faqQuery = queryOptions({
  queryKey: faqKey,
  queryFn: async (): Promise<FaqEntry[]> => {
    const { data, error, response } = await api.GET('/api/v1/platform/faq');
    if (!data) throw failure('listFaqOnPlatform', error, response.status);
    return data;
  },
});

/** Adds a question, or changes one by its id. */
export const useSaveFaq = () =>
  useChange(faqKey, async ({ id, body }: { id: string | null; body: FaqEntryInput }) => {
    const { data, error, response } = id
      ? await api.PUT('/api/v1/platform/faq/{faqId}', { params: { path: { faqId: id } }, body })
      : await api.POST('/api/v1/platform/faq', { body });
    if (!data) throw failure(id ? 'changeFaq' : 'createFaq', error, response.status);
    return data;
  });

export const useDeleteFaq = () =>
  useChange(faqKey, async (id: string) => {
    const { error, response } = await api.DELETE('/api/v1/platform/faq/{faqId}', {
      params: { path: { faqId: id } },
    });
    if (!response.ok) throw failure('deleteFaq', error, response.status);
  });

export type Announcement = Schemas['Announcement'];
type AnnouncementInput = Schemas['AnnouncementInput'];

const announcementsKey = ['platform', 'announcements'] as const;

/** Every announcement, also those still to be published. */
export const announcementsQuery = queryOptions({
  queryKey: announcementsKey,
  queryFn: async (): Promise<Announcement[]> => {
    const { data, error, response } = await api.GET('/api/v1/platform/announcements');
    if (!data) throw failure('listAnnouncementsOnPlatform', error, response.status);
    return data;
  },
});

export const useSaveAnnouncement = () =>
  useChange(
    announcementsKey,
    async ({ id, body }: { id: string | null; body: AnnouncementInput }) => {
      const { data, error, response } = id
        ? await api.PUT('/api/v1/platform/announcements/{announcementId}', {
            params: { path: { announcementId: id } },
            body,
          })
        : await api.POST('/api/v1/platform/announcements', { body });
      if (!data)
        throw failure(id ? 'changeAnnouncement' : 'createAnnouncement', error, response.status);
      return data;
    },
  );

export const useDeleteAnnouncement = () =>
  useChange(announcementsKey, async (id: string) => {
    const { error, response } = await api.DELETE(
      '/api/v1/platform/announcements/{announcementId}',
      { params: { path: { announcementId: id } } },
    );
    if (!response.ok) throw failure('deleteAnnouncement', error, response.status);
  });

export type PlatformContractField = Schemas['PlatformContractField'];

const contractFieldsKey = ['platform', 'contract-fields'] as const;

/** Every value the firms type in their contracts, with the answers proposed. */
export const contractFieldsQuery = queryOptions({
  queryKey: contractFieldsKey,
  queryFn: async (): Promise<PlatformContractField[]> => {
    const { data, error, response } = await api.GET('/api/v1/platform/contract-fields');
    if (!data) throw failure('listContractFieldsOnPlatform', error, response.status);
    return data;
  },
});

/** The answers proposed for one value anew; none leaves it to be typed freely. */
export const useSaveFieldAnswers = () =>
  useChange(contractFieldsKey, async ({ name, options }: { name: string; options: string[] }) => {
    const { data, error, response } = await api.PUT('/api/v1/platform/contract-fields/{name}', {
      params: { path: { name } },
      body: { options },
    });
    if (!data) throw failure('changeFieldAnswers', error, response.status);
    return data;
  });
